import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import { Table, TableBody, TableCell, TableHeader, TableRow } from "../../components/ui/table";
import { Button } from "react-bootstrap";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { faCopy } from "@fortawesome/free-regular-svg-icons";
import CustomPagination from "../../components/common/pagination";
import useClipboard from "../../hooks/useClipboard";

// ── Shared hooks / components / utils ────────────────────────────────────────
import useEditorField from '../../hooks/Useeditorfield';
import useDeleteModal from '../../hooks/Usedeletemodal';
import EditorField from '../../components/content/EditorField';
import {
    convertContentToHTML,
    convertHTMLToEditorState,
    renderHTMLContent,
} from '../../utils/editorUtils';

// ─────────────────────────────────────────────────────────────────────────────

const HeavenHellContent = () => {
    // ── Server / UI state ────────────────────────────────────────────────────
    const [visible, setVisible] = useState(false);
    const [pagination, setPagination] = useState(null);
    const [id, setId] = useState();
    const [loading, setLoading] = useState(true);
    const [filteredData, setFilteredData] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(15);

    const { copyToClipboard } = useClipboard();

    const indexOfFirstItem = (currentPage - 1) * itemsPerPage;
    const currentItems = filteredData;

    // ── Editor hook — English question (rich text, max 25 chars) ─────────────
    const editor = useEditorField({
        charLimit: 25,
        richText: true,
        clearStyleOnSpace: true,
    });

    // ── Editor hook — Hinglish question (rich text, max 25 chars) ────────────
    const hinglishEditor = useEditorField({
        charLimit: 25,
        richText: true,
        clearStyleOnSpace: true,
    });

    // ── Delete / selection hook ───────────────────────────────────────────────
    const {
        deleteModal,
        openDeleteModal,
        closeDeleteModal,
        selectedItems,
        selectAll,
        handleSelectAll,
        handleSelectItem,
        clearSelection,
    } = useDeleteModal(currentItems);

    // ── Data fetching ─────────────────────────────────────────────────────────
    const getData = useCallback((page = null) => {
        setLoading(true);
        const payload = {
            page: page !== null ? page : currentPage,
            limit: itemsPerPage,
        };

        axios.post('https://api.lolcards.link/api/heaven-hell/content/read', payload)
            .then((res) => {
                setPagination(res.data.pagination);
                setFilteredData(res.data.data);

                if (res.data.pagination) {
                    const totalPages = res.data.pagination.totalPages;
                    const requestedPage = page !== null ? page : currentPage;
                    if (requestedPage > totalPages && totalPages > 0) {
                        setCurrentPage(totalPages);
                        getData(totalPages);
                    }
                }
                clearSelection();
                setLoading(false);
            })
            .catch(() => {
                setLoading(false);
                toast.error("Failed to fetch data");
            });
    }, [currentPage, itemsPerPage]);

    useEffect(() => { setCurrentPage(1); getData(1); }, []);
    useEffect(() => { if (!loading) getData(); }, [currentPage]);

    // ── Delete handlers ───────────────────────────────────────────────────────
    const handleDeleteSelected = () => {
        setIsDeleting(true);
        axios.post('https://api.lolcards.link/api/admin/deleteMultiple', {
            ids: selectedItems,
            TypeId: "22",
        })
            .then(() => {
                toast.success(`Successfully deleted ${selectedItems.length} items.`);
                getData();
            })
            .catch(() => toast.error("Failed to delete selected items."))
            .finally(() => { setIsDeleting(false); closeDeleteModal(); });
    };

    const handleDelete = () => {
        axios.delete(`https://api.lolcards.link/api/heaven-hell/content/delete/${deleteModal.id}`)
            .then((res) => {
                if (currentItems.length === 1 && currentPage > 1) setCurrentPage(currentPage - 1);
                else getData(currentPage);
                closeDeleteModal();
                toast.success(res.data.message);
            })
            .catch(() => toast.error("An error occurred. Please try again."));
    };

    // ── Modal helpers ─────────────────────────────────────────────────────────
    const openModal = (mode = 'add') => {
        if (mode === 'add') {
            setId(undefined);
            editor.resetEditor();
            hinglishEditor.resetEditor();
        }
        setVisible(true);
    };

    const closeModal = () => {
        if (isSubmitting) return;
        editor.resetEditor();
        hinglishEditor.resetEditor();
        setVisible(false);
    };

    // ── Submit ────────────────────────────────────────────────────────────────
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isSubmitting) return;

        if (!editor.validateContent()) return;
        if (!hinglishEditor.validateContent()) return;

        try {
            setIsSubmitting(true);
            const htmlContent = convertContentToHTML(editor.editorState.getCurrentContent());

            const payload = {
                Content: htmlContent,
                enhiContent: hinglishEditor.plainText.trim(),
            };

            if (id) {
                await axios.patch(
                    `https://api.lolcards.link/api/heaven-hell/content/update/${id}`,
                    payload
                );
                toast.success('Question updated successfully');
            } else {
                await axios.post(
                    'https://api.lolcards.link/api/heaven-hell/content/create',
                    payload
                );
                toast.success('Question created successfully');
            }

            editor.resetEditor();
            hinglishEditor.resetEditor();
            setId(undefined);
            setVisible(false);
            getData(currentPage);
        } catch (err) {
            console.error(err);
            toast.error("An error occurred. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    // ── Edit ──────────────────────────────────────────────────────────────────
    const handleEdit = (content) => {
        editor.setEditorState(convertHTMLToEditorState(content.Content || ''));
        hinglishEditor.setEditorState(convertHTMLToEditorState(content.enhiContent || ''));
        setId(content._id);
        setVisible(true);
    };

    const clearAllFilters = () => {
        clearSelection();
        setCurrentPage(1);
        getData(1);
        toast.info("All filters cleared");
    };

    // ── Render ────────────────────────────────────────────────────────────────
    if (loading) return (
        <div style={{ height: '80vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <div className="border p-4 flex items-center space-x-2 rounded-md">
                <div className="w-10 h-10 border-2 border-gray-300 rounded-full animate-spin"
                    style={{ borderTop: "2px solid #FA4B56" }} />
            </div>
        </div>
    );

    return (
        <div>
            <PageBreadcrumb pageTitle="HeavenHell Question" />

            <div className="space-y-6 sticky left-0">
                <div className="rounded-2xl overflow-auto border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]"
                    style={{ minHeight: "600px" }}>

                    {/* ── Top toolbar ── */}
                    <div className="px-6 pt-5">
                        <div className="flex justify-between items-center px-4 py-3 mt-4 gap-4">

                            {/* Left: bulk delete / clear */}
                            <div className="flex gap-4">
                                {selectedItems.length > 0 && (
                                    <Button
                                        onClick={() => openDeleteModal(null, true)}
                                        disabled={isDeleting}
                                        variant="danger"
                                        className="d-flex align-items-center gap-2 py-1"
                                        style={{ fontSize: "14px", color: "#f13838", border: "none" }}
                                    >
                                        {isDeleting ? (
                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        ) : (
                                            <>
                                                <FontAwesomeIcon icon={faTrash} className="pe-3" />
                                                <span>DELETE SELECTED ({selectedItems.length})</span>
                                            </>
                                        )}
                                    </Button>
                                )}
                                {selectedItems.length > 0 && (
                                    <Button
                                        onClick={clearAllFilters}
                                        variant="outline-secondary"
                                        className="d-flex align-items-center gap-2 py-1 border-0 bg-transparent"
                                        style={{ fontSize: "14px", color: "#f13838" }}
                                    >
                                        CLEAR ALL
                                    </Button>
                                )}
                            </div>

                            {/* Right: add button */}
                            <div className="flex gap-3">
                                <Button
                                    onClick={() => openModal('add')}
                                    className="rounded-md border-0 shadow-md px-4 py-2 text-white"
                                    style={{ background: "#FA4B56" }}
                                >
                                    <FontAwesomeIcon icon={faPlus} className="pe-2" /> Add Question
                                </Button>
                            </div>
                        </div>
                    </div>

                    {/* ── Table ── */}
                    <div className="p-4 border-gray-100 dark:border-gray-800 sm:p-6 overflow-auto">
                        <div className="space-y-6 rounded-lg xl:border dark:border-gray-800">
                            <Table>
                                <TableHeader className="border-b border-gray-100 dark:border-white/[0.05]">
                                    <TableRow>
                                        <TableCell isHeader className="py-7 font-medium text-gray-500 px-2 border-r border-gray-200 dark:border-gray-700 w-10">
                                            <div className="flex items-center justify-center">
                                                <input type="checkbox"
                                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                                    checked={selectAll} onChange={handleSelectAll} />
                                            </div>
                                        </TableCell>
                                        <TableCell isHeader className="py-4 font-medium text-gray-500 px-2 border-r border-gray-200 dark:border-gray-700">Index</TableCell>
                                        <TableCell isHeader className="py-7 font-medium text-gray-500 px-2 border-r border-gray-200 dark:border-gray-700">HeavenHell Question</TableCell>
                                        <TableCell isHeader className="py-7 font-medium text-gray-500 px-2 border-r border-gray-200 dark:border-gray-700">Hinglish Question</TableCell>
                                        <TableCell isHeader className="py-7 font-medium text-gray-500 px-2 border-r border-gray-200 dark:border-gray-700">Actions</TableCell>
                                    </TableRow>
                                </TableHeader>

                                <TableBody className="divide-y divide-gray-100 dark:divide-white/[0.05] text-center">
                                    {currentItems && currentItems.length > 0 ? (
                                        currentItems.map((content, index) => (
                                            <TableRow key={content._id}>
                                                <TableCell className="py-3 px-2 border-r border-gray-200 dark:border-gray-700">
                                                    <div className="flex items-center justify-center">
                                                        <input type="checkbox"
                                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                                            checked={selectedItems.includes(content._id)}
                                                            onChange={() => handleSelectItem(content._id)} />
                                                    </div>
                                                </TableCell>

                                                <TableCell className="text-center px-2 border-r border-gray-200 dark:border-gray-700 dark:text-gray-400">
                                                    {indexOfFirstItem + index + 1}
                                                </TableCell>

                                                {/* English Question */}
                                                <TableCell className="py-3 px-2 border-r border-gray-200 dark:border-gray-700 dark:text-gray-400">
                                                    <div className="flex items-center justify-center gap-4">
                                                        {renderHTMLContent(content.Content)}
                                                        <button
                                                            className="text-gray-600 hover:text-gray-800 shrink-0"
                                                            onClick={() => copyToClipboard(content.Content)}
                                                        >
                                                            <FontAwesomeIcon icon={faCopy} />
                                                        </button>
                                                    </div>
                                                </TableCell>

                                                {/* Hinglish Question */}
                                                <TableCell className="py-3 px-2 border-r border-gray-200 dark:border-gray-700 dark:text-gray-400">
                                                    <div className="flex items-center justify-center gap-4">
                                                        {renderHTMLContent(content.enhiContent)}
                                                        <button
                                                            className="text-gray-600 hover:text-gray-800 shrink-0"
                                                            onClick={() => copyToClipboard(content.enhiContent)}
                                                        >
                                                            <FontAwesomeIcon icon={faCopy} />
                                                        </button>
                                                    </div>
                                                </TableCell>

                                                <TableCell className="py-3 px-2 border-r border-gray-200 dark:border-gray-700">
                                                    <div className="flex align-middle justify-center gap-4 h-full">
                                                        <button style={{ color: "#0385C3" }} onClick={() => handleEdit(content)}>
                                                            <FontAwesomeIcon icon={faEdit} className="text-lg" />
                                                        </button>
                                                        <button className="text-red-600" onClick={() => openDeleteModal(content._id)}>
                                                            <FontAwesomeIcon icon={faTrash} className="text-lg" />
                                                        </button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={5} className="text-center pt-5 pb-4 dark:text-gray-400">No Data Found</td>
                                        </tr>
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Pagination ── */}
            <CustomPagination
                currentPage={currentPage}
                totalPages={pagination ? pagination.totalPages : Math.ceil(filteredData.length / itemsPerPage)}
                onPageChange={(page) => {
                    setCurrentPage(page);
                    clearSelection();
                    getData(page);
                }}
                itemsPerPage={itemsPerPage}
                totalItems={pagination ? pagination.total : filteredData.length}
            />

            {/* ── Add / Edit modal ── */}
            {visible && (
                <div className="fixed inset-0 z-[99999] flex items-center justify-center">
                    <div className="fixed inset-0 bg-black bg-opacity-50" onClick={closeModal} />
                    <div className="relative bg-white rounded-lg w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">

                        <div className="px-6 py-4 border-b">
                            <h3 className="text-xl font-semibold">
                                {id ? "Edit Question" : "Add Question"}
                            </h3>
                        </div>

                        <div className="px-6 py-4">
                            <form onSubmit={handleSubmit}>

                                {/* ── English question editor ── */}
                                <EditorField
                                    label="Question (English)"
                                    hint="Type your question and use the emoji picker for emojis. (Max 25 characters)"
                                    richText={true}
                                    disabled={isSubmitting}
                                    hook={editor}
                                />

                                {/* ── Hinglish question editor ── */}
                                <EditorField
                                    label="Question (Hinglish)"
                                    hint="Type your Hinglish question and use the emoji picker for emojis. (Max 25 characters)"
                                    richText={true}
                                    disabled={isSubmitting}
                                    hook={hinglishEditor}
                                />

                                <div className="flex gap-4 mt-4">
                                    <button
                                        type="button"
                                        onClick={closeModal}
                                        disabled={isSubmitting}
                                        className="w-1/2 py-2 px-4 bg-gray-100 text-black rounded-lg hover:bg-gray-200 transition-colors duration-200 disabled:opacity-50"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className="w-1/2 py-2 px-4 text-white rounded-lg transition-colors duration-200 disabled:opacity-50"
                                        style={{ backgroundColor: "#FA4B56" }}
                                    >
                                        {isSubmitting ? (
                                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto" />
                                        ) : (
                                            id ? 'Update' : 'Submit'
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Delete confirmation modal ── */}
            {deleteModal.isOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999]">
                    <div className="bg-white rounded-lg p-6 w-full max-w-sm mx-4 shadow-lg">
                        <h2 className="text-xl font-semibold text-gray-800 mb-3">
                            {deleteModal.isBulk ? 'Delete Selected Items' : 'Delete Heaven-Hell Question'}
                        </h2>
                        <p className="text-gray-700 mb-6">
                            {deleteModal.isBulk
                                ? `Are you sure you want to delete ${selectedItems.length} selected items?`
                                : 'Are you sure you want to delete this Heaven-Hell Question?'}
                        </p>
                        <div className="flex justify-end gap-3">
                            <button onClick={closeDeleteModal} disabled={isDeleting}
                                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 disabled:opacity-70">
                                Cancel
                            </button>
                            <button
                                onClick={deleteModal.isBulk ? handleDeleteSelected : handleDelete}
                                disabled={isDeleting}
                                className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 disabled:opacity-70">
                                {isDeleting ? "Deleting..." : "Delete"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <ToastContainer position="top-center" className="!z-[99999]" />
        </div>
    );
};

export default HeavenHellContent;